import React, { useMemo } from 'react';
import { Box } from '@material-ui/core';
import CommentIcon from '@material-ui/icons/Comment';
import NoteTitle from './subcomponents/NoteTitle';
import ShowNoteContent from './subcomponents/ShowNoteContent';
import ShowNoteLink from './subcomponents/ShowNoteLink';
import ShowNoteImages from './subcomponents/ShowNoteImages';
import ShowNoteAudios from './subcomponents/ShowNoteAudios';
import ShowNoteDocuments from './subcomponents/ShowNoteDocuments';
import useGetMedias from './hooks/useGetMedias';

interface Props {
    note: any;
}

// Instant Note adapted to platform-v2 structure
const InstantNote: React.FC<Props> = ({ note }) => {
    const content: string | undefined = useMemo(() => note?.data?.content, [note]);
    const link: string | undefined = useMemo(() => note?.data?.link, [note]);

    const dataSource = useMemo(() => note?.data?.dataSource, [note]);
    const time: string | undefined = useMemo(() => note?.createdAt, [note]);
    const title = useMemo(() => note?.data?.text, [note]);
    const visible = useMemo(() => note?.data?.visible, [note]);
    const coords = useMemo(() => note?.data?.coords, [note]);

    const authInfo = useMemo(() => {
        const noteData = note?.data ?? {};
        if (!('firstName' in noteData) && !('username' in noteData)) return undefined;
        return 'firstName' in noteData ? { firstName: (noteData as any).firstName } : { username: (noteData as any).username };
    }, [note]);

    const mediasIds: string[] | undefined = useMemo(
        () => (Array.isArray(note?.data?.medias) ? note.data.medias.map((m: any) => m?.id).filter(Boolean) : undefined),
        [note]
    );

    const { images, audios, documents, isLoading: isLoadingMedias } = useGetMedias({ mediasIds });

    return (
        <Box style={{ display: 'grid', gap: 8, background: 'white', paddingTop: 8, paddingBottom: 8 }}>
            <NoteTitle
                icon={<CommentIcon />}
                title={title}
                time={time ?? ''}
                dataSource={dataSource}
                visible={visible}
                authInfo={authInfo}
                coords={coords}
            />

            {content && <ShowNoteContent content={content} />}

            {link && <ShowNoteLink link={link} />}

            {!isLoadingMedias && images && images.length > 0 && (
                <ShowNoteImages imagesUrls={images.map((i) => i.urlToDownload).filter((u): u is string => !!u)} />
            )}

            {!isLoadingMedias && audios && audios.length > 0 && (
                <ShowNoteAudios audios={audios.map((i) => i.urlToDownload).filter((u): u is string => !!u)} />
            )}

            {!isLoadingMedias && documents && documents.length > 0 && (
                <ShowNoteDocuments
                    documents={documents
                        .filter((d) => !!d.urlToDownload)
                        .map((d) => ({
                            urlToDownload: d.urlToDownload as string,
                            name: (d.name as string | undefined) || ((d.urlToDownload as string).split('/').pop() || 'document'),
                        }))}
                />
            )}
        </Box>
    );
};

export default InstantNote;
